/**
 * Company: SPHMedia
 * Description: [Some custom React Hook for inline video player functionality]
 */

import type { VideoPlayerRef } from "@typings/Brightcove.d";
import {
  Dispatch,
  RefObject,
  SetStateAction,
  useCallback,
  useState,
} from "react";

let userMutePreference = true;

type useInlineVideoPlayerData = {
  isPlaying: boolean;
  toggleMute: () => void;
  togglePlay: () => void;
  handlePlayerReady: (player: VideoPlayerRef) => void;
  handlePlayerExpand: () => void;
};

type useInlineVideoPlayerProps = {
  playerRef: RefObject<VideoPlayerRef | null>;
  pageRefValue: string;
  setIsMute: Dispatch<SetStateAction<boolean>>;
};

/**
 * Custom React Hook: useInlineVideoPlayerHook
 * Description: [Custom hook for managing inline video player functionality]
 * @param props - The props required by the hook (if any)
 * @returns data - The data returned by the hook
 */
export default function useInlineVideoPlayer({
  playerRef,
  pageRefValue,
  setIsMute,
}: useInlineVideoPlayerProps): useInlineVideoPlayerData {
  const [isPlaying, setIsPlaying] = useState(true);

  const toggleMute = useCallback(() => {
    const player = playerRef.current;

    if (!player) return;

    const muted = player.muted();

    player.muted(!muted);
    setIsMute(!muted);
    userMutePreference = !muted;
  }, [playerRef, setIsMute]);

  const togglePlay = useCallback(() => {
    const player = playerRef.current;
    if (!player) return;

    if (player.paused?.()) player.play?.();
    else player.pause();
  }, [playerRef]);

  const handlePlayerReady = useCallback(
    (player: VideoPlayerRef) => {
      playerRef.current = player;

      const handlePlay = () => {
        setIsPlaying(true);

        if (userMutePreference !== player.muted()) {
          player.muted(userMutePreference); // Sync the mute state when the player starts playing
          setIsMute(userMutePreference); // Update the state to reflect the player's mute state
        }
      };
      const handlePause = () => {
        setIsPlaying(false);
      };

      player.on("play", handlePlay);
      player.on("pause", handlePause);

      player.on("dispose", () => {
        player.off?.("play", handlePlay);
        player.off?.("pause", handlePause);
      });
    },
    [playerRef, setIsMute],
  );

  const handlePlayerExpand = useCallback(() => {
    const player = playerRef.current;

    if (!player) return;

    player.pause(); // Pause the player before opening a new tab

    const videoId = player.mediainfo?.id;
    if (!videoId) return;

    const qs = new URLSearchParams({ ref: pageRefValue }).toString();
    window.open(
      `/quick-news/video/${videoId}?${qs}`,
      "_blank",
      "noopener,noreferrer",
    );
  }, [playerRef, pageRefValue]);

  return {
    isPlaying,
    toggleMute,
    togglePlay,
    handlePlayerReady,
    handlePlayerExpand,
  };
}
